import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../../utils/api';
import { noticeMatchesPath, NOTICE_SEVERITY_MAP } from './statusAreas';
import './StatusNoticeBanner.css';

/** localStorage key prefix; the stored value is the dismissed notice's version. */
const DISMISS_PREFIX = 'status_notice_dismissed:';
/** How often to re-poll active notices so admin toggles propagate to open tabs. */
const REFRESH_MS = 60000;

/**
 * Site-wide status/maintenance banner. Renders every *live* notice whose target
 * areas match the current route, most-severe first, as a prominent coloured
 * strip at the top of the page. Distinct from the low-key news banner: this is
 * for "something is down / being rebuilt" and can be locked open (non-
 * dismissible) for critical outages.
 *
 * Dismissal is remembered per notice *version* (``updated_at``) in localStorage,
 * so editing a notice re-shows it. Mounted once at the app layout level; page
 * targeting is decided client-side via {@link noticeMatchesPath}.
 *
 * @returns {JSX.Element|null} The banner stack, or null when nothing matches.
 */
export default function StatusNoticeBanner() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [notices, setNotices] = useState([]);
  // Map of notice id -> the version (updated_at) the user dismissed, hydrated
  // from localStorage so dismissals survive reloads but re-show after an edit.
  const [dismissed, setDismissed] = useState({});

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      fetch(`${API_URL}/api/notices/active`)
        .then((r) => (r.ok ? r.json() : []))
        .then((data) => {
          if (!cancelled && Array.isArray(data)) setNotices(data);
        })
        .catch(() => {});
    };
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  // Hydrate the dismissed map from localStorage whenever the notice set changes.
  useEffect(() => {
    const next = {};
    for (const n of notices) {
      try {
        const v = localStorage.getItem(DISMISS_PREFIX + n.id);
        if (v) next[n.id] = v;
      } catch { /* ignore — treat as not dismissed */ }
    }
    setDismissed(next);
  }, [notices]);

  /** Dismiss a notice, remembering its version so an edit re-shows it. */
  const dismiss = (n) => {
    const version = String(n.updated_at);
    try {
      localStorage.setItem(DISMISS_PREFIX + n.id, version);
    } catch { /* dismissal just won't persist */ }
    setDismissed((d) => ({ ...d, [n.id]: version }));
  };

  /** Open a notice's link — external URLs in a new tab, internal via the router. */
  const openLink = (url) => {
    if (/^https?:\/\//i.test(url)) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      navigate(url);
    }
  };

  const visible = notices.filter(
    (n) => noticeMatchesPath(n.areas, location.pathname)
      && !(n.dismissible && dismissed[n.id] === String(n.updated_at)),
  );
  if (visible.length === 0) return null;

  return (
    <div className="status-notices">
      {visible.map((n) => {
        const sev = NOTICE_SEVERITY_MAP[n.severity] || NOTICE_SEVERITY_MAP.info;
        return (
          <div key={n.id} className={`status-notice status-notice-${n.severity}`} role="alert">
            <span className="status-notice-icon" aria-hidden="true">{sev.emoji}</span>
            <div className="status-notice-text">
              <strong className="status-notice-title">{n.title}</strong>
              {n.message && <span className="status-notice-message">{n.message}</span>}
            </div>
            {n.link_url && (
              <button
                type="button"
                className="status-notice-link"
                onClick={() => openLink(n.link_url)}
              >
                {n.link_label || t('notices.readMore')}
              </button>
            )}
            {n.dismissible && (
              <button
                type="button"
                className="status-notice-close"
                onClick={() => dismiss(n)}
                aria-label={t('profile.close')}
                title={t('profile.close')}
              >
                ✕
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
